<template>
  <v-dialog max-width="600" v-model="show" persistent="">
    <v-card>
      <v-card-title class="headline">{{
        $t('components.questionCreate.newQuestion')
      }}</v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12>
                <v-textarea
                  :auto-grow="true"
                  :label="$t('fields.question')"
                  :rows="1"
                  outline
                  v-model="question.question"
                  :rules="[
                    v =>
                      !!v ||
                      $t(
                        'components.questionCreate.validation.question.required'
                      ),
                    v =>
                      v.length <= 2000 ||
                      $t(
                        'components.questionCreate.validation.question.length'
                      ),
                  ]"
                  counter="2000"
                ></v-textarea>
              </v-flex>
              <v-flex xs12>
                <v-layout
                  :key="key"
                  row
                  sm12
                  v-for="(answer, key) in question.answers"
                  wrap
                >
                  <v-flex pl-5>
                    <!--
											If this is the last answer and the number of answers is more than 2
											remove :rules. As soon as the user types something inside, the code
											will add an empty answer and this answer will not be last and will be
											validated.
										-->
                    <v-textarea
                      v-if="key === question.answers.length - 1 && key >= 2"
                      :auto-grow="true"
                      :label="$t('labels.answer')"
                      :placeholder="answerPlaceholder(key)"
                      :rows="1"
                      @keyup="answerNewOrDelete(answer, key)"
                      outline
                      v-model="answer.label"
                    ></v-textarea>
                    <v-textarea
                      v-else
                      :auto-grow="true"
                      :label="$t('labels.answer')"
                      :placeholder="answerPlaceholder(key)"
                      :rows="1"
                      @keyup="answerNewOrDelete(answer, key)"
                      outline
                      :rules="answer_rules"
                      v-model="answer.label"
                    ></v-textarea>
                  </v-flex>
                  <v-flex>
                    <v-tooltip bottom v-if="question.answers.length > 2">
                      <v-btn
                        @click="answerDeleteCheck(key)"
                        icon
                        ripple
                        slot="activator"
                      >
                        <v-icon color="grey lighten-1"> fa-trash </v-icon>
                      </v-btn>
                      <span>{{ $t('buttons.delete') }}</span>
                    </v-tooltip>
                  </v-flex>
                </v-layout>
              </v-flex>
              <v-flex xs12>
                <v-checkbox
                  :label="$t('labels.multiple_choices_enabled')"
                  color="primary"
                  v-model="question.multiple_choices_enabled"
                ></v-checkbox>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                  :label="$t('fields.minimum_choices')"
                  :mask="'##'"
                  outline
                  v-if="question.multiple_choices_enabled"
                  v-model="question.min_choices"
                  :rules="min_choices_rules"
                ></v-text-field>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                  :label="$t('fields.maximum_choices')"
                  :mask="'##'"
                  outline
                  v-if="question.multiple_choices_enabled"
                  v-model="question.max_choices"
                  :hint="$t('labels.maximumChoices')"
                  :rules="max_choices_rules"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-checkbox
                  :label="$t('labels.abstention')"
                  color="primary"
                  v-model="question.abstention"
                ></v-checkbox>
              </v-flex>
              <v-flex xs12>
                <v-checkbox
                  :label="$t('labels.randomizeOptions')"
                  color="primary"
                  v-model="question.randomize_options"
                ></v-checkbox>
              </v-flex>
              <v-flex xs12>
                <v-tooltip left>
                  <template v-slot:activator="{ on }">
                    <div v-on="on">
                      <v-radio-group
                        v-model="question.anonymous"
                        row
                        label="Anonymous:"
                      >
                        <v-radio
                          label="Yes"
                          color="primary"
                          :value="true"
                        ></v-radio>
                        <v-radio
                          label="No"
                          color="primary"
                          :value="false"
                        ></v-radio>
                      </v-radio-group>
                    </div>
                  </template>
                  <span>{{ $t('tooltips.questionAnonymity') }}</span>
                </v-tooltip>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  v-if="quorum"
                  :label="$t('labels.quorum')"
                  :rules="quorum_rules"
                  outline
                  v-model="question.quorum_value"
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click.stop="close" color="error">{{
          $t('buttons.close')
        }}</v-btn>
        <v-btn @click="save" color="success">{{ $t('buttons.save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Question from '@/models/Question.js'

export default {
  props: {
    value: Boolean,
    quorum: Boolean,
  },

  data() {
    return {
      question: new Question(),
      valid: false,
    }
  },

  computed: {
    show: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },

    quorum_rules() {
      let rules = []

      if (
        this.quorum === true &&
        this.question.quorum_value !== null &&
        this.question.quorum_value.trim() != ''
      ) {
        rules = [
          v =>
            Number.isInteger(Number(v)) ||
            this.$t('components.questionCreate.validation.quorum.integer'),
          v =>
            v >= 1 ||
            this.$t('components.questionCreate.validation.quorum.min'),
        ]
      }

      return rules
    },

    answer_rules() {
      const rules = []

      if (this.question.answers.length < 2) {
        const rule = () =>
          this.$t('components.questionCreate.validation.answers.min')

        rules.push(rule)
      } else {
        const rule = v =>
          !!v ||
          this.$t('components.questionCreate.validation.answers.required')

        rules.push(rule)
      }

      return rules
    },

    min_choices_rules() {
      let rules = []

      if (this.question.multiple_choices_enabled === true) {
        rules = [
          v =>
            v >= 1 ||
            this.$t('components.questionCreate.validation.minChoices.min'),
          v =>
            v < this.question.answers.length ||
            this.$t('components.questionCreate.validation.minChoices.length'),
        ]
      }

      return rules
    },

    max_choices_rules() {
      let rules = []

      if (this.question.multiple_choices_enabled === true) {
        rules = [
          v =>
            v >= 0 ||
            this.$t('components.questionCreate.validation.maxChoices.min'),
          v =>
            v <= this.question.answers.length ||
            this.$t('components.questionCreate.validation.maxChoices.length'),
        ]
      }

      return rules
    },
  },

  methods: {
    close() {
      this.$verify(this.$t('alerts.closeQuestionCreate'), result => {
        if (result) {
          this.show = false
        }
      })
    },

    save() {
      if (!this.$refs.form.validate()) return

      let questionValidation = this.validateQuestionAnswers()
      if (questionValidation != '') {
        this.$alert(questionValidation)
        return
      }

      this.$emit('question-store', this.question)

      // Clear form.
      this.question = new Question()

      // Reset validation.
      this.$refs.form.resetValidation()

      // Close modal.
      this.show = false
    },

    // Display placeholder only on last answer.
    answerPlaceholder(key) {
      if (key == this.question.answers.length - 1) {
        return this.$t('fields.addOption')
      }
    },

    answerNewOrDelete(answer, key) {
      // If there are more than two answers, and the current answer is last, then delete it.
      if (
        this.question.answers.length > 2 &&
        key != this.question.answers.length - 1 &&
        answer.label.trim() === ''
      ) {
        this.question.removeAnswer(key)
      }

      // If the current answer is last and it has some text inside, create a new empty answer.
      if (
        key == this.question.answers.length - 1 &&
        answer.label.trim() != ''
      ) {
        this.question.addBlankAnswer()
      }
    },

    // Ask for confirmation, before deleting the answer.
    answerDeleteCheck(key) {
      this.$verify(this.$t('alerts.optionDeletionQuestion'), result => {
        if (result) {
          this.question.removeAnswer(key)
        }
      })
    },

    validateQuestionAnswers() {
      if (this.question.answers.length > 0) {
        let answers = []
        for (let answer of this.question.answers) {
          if (answers.includes(answer.label)) {
            return this.$t('alerts.optionsDuplicate')
          } else {
            answers.push(answer.label)
          }
        }
      }
      return ''
    },
  },
}
</script>
